/**
 * src/services/attendance.api.ts 의 짝.
 *
 * 휴가 유형과 근무 유형은 각자 승인 필요와 첨부 칸을 정한다. 승인이 필요 없으면 넣는 즉시 승인이다.
 * 승인이 필요한 휴가 유형 중 연차를 깎는 것은 설정의 일수 상한과 신청 기한을 모두 충족하면 자동
 * 승인이고, 남은 연차보다 긴 신청은 거절한다. 달력에는 승인된 것만 오른다.
 *
 * 휴가 유형은 사무소가 정한다(`/vacation-kinds/`). 하루보다 짧은 유형(반차·반반차)은 그 하루의
 * 시각 범위로 받고, 길이가 하루 기준 시간 × 유형의 날수와 맞아야 한다. 차감 일수는 서버가 센다 —
 * 종일은 근무일 수, 시각 범위는 그 시간 ÷ 하루 기준 시간(점심을 빼지 않는다).
 *
 * 첨부파일은 먼저 올려 id 를 받고, 신청할 때 그 id 를 보낸다(backend `app/attendance/attachments.py`).
 */
import { HttpResponse, http } from "msw";
import { vacationDays } from "@/lib/leave";
import type {
  AttendanceAttachment,
  AttendanceDecision,
  GrantInput,
  GrantPatch,
  HolidayInput,
  ScheduleCategory,
  ScheduleCategoryInput,
  ScheduleCategoryPatch,
  ScheduleInput,
  UserSchedule,
  Vacation,
  VacationGrant,
  VacationInput,
  VacationKind,
  VacationKindInput,
  VacationKindPatch,
} from "@/types";
import { db } from "../db";
import { fail, latency, requireHr, requireUser } from "../http";

const BASE = "/api/v1";
let seq = 0;

const holidayDates = () => new Set(db.holidays.map((holiday) => holiday.holiday_date));

const standardHoursOf = (userId: string) =>
  db.users.find((u) => u.id === userId)?.standard_daily_hours ?? 8;

const nameOf = (userId: string) => db.users.find((u) => u.id === userId)?.name ?? userId;

/** 서버처럼 시간대 없이 온 시각은 사무소 시각(+09:00)으로 읽는다. */
const inOffice = (moment: string) =>
  /[+-]\d{2}:\d{2}$|Z$/.test(moment) ? moment : `${moment.slice(0, 19)}+09:00`;

/** 그 달에 하루라도 걸치는가 — 달력은 달마다 받는다. */
const inMonth = (start: string, end: string, year: number, month: number) => {
  const key = `${year}-${String(month).padStart(2, "0")}`;
  return start.slice(0, 7) <= key && key <= end.slice(0, 7);
};

/** 달력용 — 사유와 결정 내용을 뺀 사본. */
const toCalendar = (vacation: Vacation) => ({
  id: vacation.id,
  user_id: vacation.user_id,
  user_name: vacation.user_name,
  kind_id: vacation.kind_id,
  kind_name: vacation.kind_name,
  kind_color: vacation.kind_color,
  start_date: vacation.start_date,
  end_date: vacation.end_date,
  start_time: vacation.start_time,
  end_time: vacation.end_time,
  days: vacation.days,
});

/** 새 휴가 유형 · 근무 유형은 맨 끝이다. */
const nextOrder = (rows: { sort_order: number }[]) =>
  Math.max(0, ...rows.map((row) => row.sort_order)) + 10;

const ATTACHMENTS = 5;
const ATTACHMENT_BYTES = 10 * 1024 * 1024;
const ATTACHMENT_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif", "application/pdf"];

/**
 * 서버처럼 올린 사람의 아직 붙지 않은 파일만 붙인다. 문제가 있으면 응답을 준다. 사전 첨부를 켠 유형은
 * 신청할 때 반드시 붙이고, 끈 유형은 신청할 때 붙이지 않는다(stage 가 "before" 일 때만 이 규칙을 본다).
 */
function attachFiles(
  stage: "before" | "after",
  required: boolean,
  ids: string[] | undefined,
  userId: string,
  owner: string
): AttendanceAttachment[] | Response {
  const wanted = [...new Set(ids ?? [])];
  if (stage === "before" && required && wanted.length === 0)
    return fail(422, "이 유형은 첨부파일이 필요합니다.");
  if (stage === "before" && !required && wanted.length > 0)
    return fail(422, "이 유형은 첨부파일을 받지 않습니다.");
  if (wanted.length > ATTACHMENTS) return fail(422, "첨부파일은 한 신청에 5개까지입니다.");
  const rows = db.attendanceAttachments.filter(
    (row) => wanted.includes(row.id) && row.uploaded_by === userId && row.owner === null
  );
  if (rows.length !== wanted.length) return fail(404, "첨부파일을 찾을 수 없습니다.");
  for (const row of rows) {
    row.owner = owner;
    row.stage = stage;
  }
  return rows.map(({ id, file_name, content_type, byte_size, url }) => ({
    id,
    stage,
    file_name,
    content_type,
    byte_size,
    url,
  }));
}

/**
 * 부여 한 건의 사용 · 소멸 · 잔여 — 그 기간에 든 승인·대기 휴가 중 연차를 깎는 유형을 센다. 만료일이 지난
 * 지급분은 남은 날이 소멸해 잔여가 0 이다.
 */
function withUsage(grant: Omit<VacationGrant, "used" | "expired" | "remaining">): VacationGrant {
  const used = db.vacations
    .filter(
      (vacation) =>
        vacation.user_id === grant.user_id &&
        vacation.deducts_annual &&
        (vacation.status === "approved" || vacation.status === "pending") &&
        vacation.start_date >= grant.period_from &&
        vacation.start_date < grant.period_to
    )
    .reduce((sum, vacation) => sum + vacation.days, 0);
  const left = Math.round((grant.granted + grant.adjusted - grant.prior_used - used) * 100) / 100;
  const ended = grant.period_to <= new Date().toISOString().slice(0, 10);
  return {
    ...grant,
    used: Math.round(used * 100) / 100,
    expired: ended ? Math.max(0, left) : 0,
    remaining: ended ? Math.min(0, left) : left,
  };
}

const remainingOf = (userId: string) =>
  db.vacationGrants
    .filter((grant) => grant.user_id === userId)
    .reduce((sum, grant) => sum + withUsage(grant).remaining, 0);

const HALF_HOUR = /^([01]\d|2[0-3]):(00|30)(:00)?$/;

const minutesOf = (time: string) => Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5));

/**
 * 한 번 쓸 때의 날수 — 비우면(null) 기간, 1 이상의 정수면 일수 고정, 1 미만이면 0.25일 간격(시간 단위).
 * 시간 단위의 시작 · 끝 시각을 따로 고르는 휴가 유형은 없다. 서버도 같은 규칙으로 422 를 낸다.
 */
const badUnit = (unit: number | null | undefined) => {
  if (unit === null || unit === undefined) return null;
  const days = Number(unit);
  const ok = days >= 1 ? Number.isInteger(days) : [0.25, 0.5, 0.75].includes(days);
  return ok ? null : fail(422, "일수는 0.25일 간격(1일 미만)이나 1일 이상의 정수로 정해 주세요.");
};

/**
 * 사후 첨부 기한 — 켰으면 1~90일 정수가 있어야 하고, 껐으면 비운다. 서버도 같은 규칙으로 422 를 낸다.
 */
function afterDays(on: boolean, days: number | null | undefined): number | null | Response {
  if (!on) return null;
  if (days == null || !Number.isInteger(days) || days < 1 || days > 90)
    return fail(422, "사후 첨부 기한은 1~90일 사이로 정해 주세요.");
  return days;
}

export const attendanceHandlers = [
  /* ── 휴가 ────────────────────────────────────────────────────────── */

  http.get(`${BASE}/vacations/`, async () => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const results = db.vacations
      .filter((vacation) => vacation.user_id === user.id)
      .sort((a, b) => (a.start_date < b.start_date ? 1 : -1));
    return HttpResponse.json({ results });
  }),

  http.post(`${BASE}/vacations/`, async ({ request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const input = (await request.json()) as VacationInput;
    const kind = db.vacationKinds.find((row) => row.id === input.kind_id);
    if (!kind) return fail(404, "없는 휴가 유형입니다.");
    if (kind.reason_required && !input.reason?.trim())
      return fail(422, "이 휴가 유형은 사유가 필요합니다.");
    if (!input.start_date || !input.end_date) return fail(422, "기간을 고르세요.");
    if (input.end_date < input.start_date) return fail(422, "기간의 끝이 시작보다 빨라요.");
    const standard = standardHoursOf(user.id);
    // 서버처럼 하루보다 짧은 유형(반차·반반차)은 시작·종료 시각으로, 그 밖은 종일로 받는다.
    const partial = !!(input.start_time && input.end_time);
    const partialKind = kind.unit_days !== null && kind.unit_days < 1;
    if (partialKind && !partial)
      return fail(422, "이 휴가 유형은 하루 안의 시작 시각으로 신청합니다.");
    if (!partialKind && partial) return fail(422, "이 휴가 유형은 종일로 신청합니다.");
    if (partial) {
      if (input.start_date !== input.end_date)
        return fail(422, "시각을 고르면 하루만 쓸 수 있어요.");
      if (!HALF_HOUR.test(input.start_time!) || !HALF_HOUR.test(input.end_time!))
        return fail(422, "시각은 30분 단위로 고르세요.");
      if (minutesOf(input.end_time!) <= minutesOf(input.start_time!))
        return fail(422, "종료 시각이 시작보다 빨라요.");
      if (
        minutesOf(input.end_time!) - minutesOf(input.start_time!) !==
        kind.unit_days! * standard * 60
      )
        return fail(422, "휴가 시간이 이 유형의 길이(하루 기준 근무시간 × 날수)와 맞지 않습니다.");
    }
    const days = vacationDays(
      {
        start_date: input.start_date,
        end_date: input.end_date,
        start_time: partial ? input.start_time : null,
        end_time: partial ? input.end_time : null,
      },
      standard,
      holidayDates()
    );
    if (days <= 0) return fail(422, "고른 기간에 근무일이 없어요.");
    // 일수를 고정한 유형은 근무일이 그 수만큼이어야 한다 — 일수를 고르는 것은 연차뿐이다.
    if (
      kind.unit_days !== null &&
      kind.unit_days >= 1 &&
      !kind.is_annual &&
      days !== kind.unit_days
    )
      return fail(422, `이 휴가 유형은 ${kind.unit_days}일로 신청합니다.`);
    if (kind.deducts_annual && days > remainingOf(user.id))
      return fail(422, "남은 연차보다 많아요.");

    const id = `vacation-new-${++seq}`;
    const attachments = attachFiles(
      "before",
      kind.attachment_before,
      input.attachment_ids,
      user.id,
      id
    );
    if (attachments instanceof Response) return attachments;
    const auto = !kind.requires_approval;
    const stamp = new Date().toISOString();
    const made: Vacation = {
      id,
      user_id: user.id,
      user_name: nameOf(user.id),
      kind_id: kind.id,
      kind_name: kind.name,
      kind_color: kind.color,
      deducts_annual: kind.deducts_annual,
      attachments,
      attachment_due: kind.attachment_after,
      start_date: input.start_date,
      end_date: input.end_date,
      start_time: partial ? `${input.start_time!.slice(0, 5)}:00` : null,
      end_time: partial ? `${input.end_time!.slice(0, 5)}:00` : null,
      days,
      reason: input.reason?.trim() ?? "",
      status: auto ? "approved" : "pending",
      auto_approved: auto,
      approver_id: null,
      decided_at: auto ? stamp : null,
      decision_note: "",
      cancel_requested_at: null,
      created_at: stamp,
    };
    db.vacations.push(made);
    return HttpResponse.json(made, { status: 201 });
  }),

  /** 사후 첨부 — 다녀온 뒤 내는 서류. 사후 첨부를 켠 유형의, 취소 · 반려되지 않은 내 신청에만. */
  http.post(`${BASE}/vacations/:id/attachments/`, async ({ params, request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const found = db.vacations.find((v) => v.id === params.id && v.user_id === user.id);
    if (!found) return fail(404, "없는 휴가입니다.");
    const kind = db.vacationKinds.find((row) => row.id === found.kind_id);
    if (!kind?.attachment_after) return fail(409, "이 유형은 사후 첨부를 받지 않습니다.");
    if (found.status !== "pending" && found.status !== "approved")
      return fail(409, "취소되거나 반려된 신청에는 첨부할 수 없습니다.");
    const { attachment_ids } = (await request.json()) as { attachment_ids: string[] };
    const attached = attachFiles("after", false, attachment_ids, user.id, found.id);
    if (attached instanceof Response) return attached;
    found.attachments = [...found.attachments, ...attached];
    found.attachment_due = false;
    return HttpResponse.json(found);
  }),

  http.get(`${BASE}/vacations/calendar/`, async ({ request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const query = new URL(request.url).searchParams;
    const year = Number(query.get("year"));
    const month = Number(query.get("month"));
    const results = db.vacations
      .filter(
        (vacation) =>
          vacation.status === "approved" &&
          inMonth(vacation.start_date, vacation.end_date, year, month)
      )
      .map(toCalendar);
    return HttpResponse.json({ results });
  }),

  http.get(`${BASE}/vacations/:id/`, async ({ params }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const found = db.vacations.find((v) => v.id === params.id && v.user_id === user.id);
    return found ? HttpResponse.json(found) : fail(404, "없는 휴가입니다.");
  }),

  http.post(`${BASE}/vacations/:id/cancel/`, async ({ params }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const found = db.vacations.find((v) => v.id === params.id && v.user_id === user.id);
    if (!found) return fail(404, "없는 휴가입니다.");
    // 승인 대기는 바로 취소되고, 승인된 건은 취소 신청으로 바뀐다.
    if (found.status === "pending") found.status = "cancelled";
    else if (found.status === "approved") found.cancel_requested_at = new Date().toISOString();
    else return fail(409, "취소할 수 없는 상태예요.");
    return HttpResponse.json(found);
  }),

  /* ── 근무 유형 — 사무소가 늘린다 ───────────────────────────────── */

  /** 결재권자 — 관리자와 HR 표시가 있는 사람. 퇴사자는 뺀다. */
  http.get(`${BASE}/attendance-deciders/`, async () => {
    await latency();
    const results = db.users
      .filter((u) => (u.role === "admin" || u.is_hr) && u.status !== "resigned")
      .sort((a, b) => Number(b.role === "admin") - Number(a.role === "admin"))
      .map((u) => ({ id: u.id, name: u.name, is_admin: u.role === "admin" }));
    return HttpResponse.json({ count: results.length, results });
  }),

  http.get(`${BASE}/schedule-categories/`, async () => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const results = [...db.scheduleCategories].sort((a, b) => a.sort_order - b.sort_order);
    return HttpResponse.json({ results });
  }),

  http.post(`${BASE}/admin/schedule-categories/`, async ({ request }) => {
    await latency();
    const admin = requireHr();
    if (admin instanceof Response) return admin;
    const input = (await request.json()) as ScheduleCategoryInput;
    const name = input.name.trim();
    if (!name) return fail(422, "이름을 적어 주세요.");
    if (db.scheduleCategories.some((row) => row.name === name))
      return fail(409, "같은 이름의 근무 유형이 있습니다.");
    const after = afterDays(input.attachment_after ?? false, input.attachment_after_days);
    if (after instanceof Response) return after;
    const made: ScheduleCategory = {
      id: `cat-${crypto.randomUUID().slice(0, 8)}`,
      name,
      color: input.color,
      requires_approval: input.requires_approval ?? false,
      attachment_before: input.attachment_before ?? false,
      attachment_after: input.attachment_after ?? false,
      attachment_after_days: after,
      all_day: input.all_day ?? false,
      sort_order: nextOrder(db.scheduleCategories),
    };
    db.scheduleCategories.push(made);
    return HttpResponse.json(made, { status: 201 });
  }),

  http.patch(`${BASE}/admin/schedule-categories/:id/`, async ({ params, request }) => {
    await latency();
    const admin = requireHr();
    if (admin instanceof Response) return admin;
    const category = db.scheduleCategories.find((row) => row.id === params.id);
    if (!category) return fail(404, "없는 근무 유형입니다.");
    const patch = (await request.json()) as ScheduleCategoryPatch;
    if (
      patch.name &&
      db.scheduleCategories.some((row) => row.id !== category.id && row.name === patch.name)
    )
      return fail(409, "같은 이름의 근무 유형이 있습니다.");
    const after = afterDays(
      patch.attachment_after ?? category.attachment_after,
      "attachment_after_days" in patch
        ? patch.attachment_after_days
        : category.attachment_after_days
    );
    if (after instanceof Response) return after;
    Object.assign(category, patch, { attachment_after_days: after });
    return HttpResponse.json(category);
  }),

  http.delete(`${BASE}/admin/schedule-categories/:id/`, async ({ params }) => {
    await latency();
    const admin = requireHr();
    if (admin instanceof Response) return admin;
    const index = db.scheduleCategories.findIndex((row) => row.id === params.id);
    if (index < 0) return fail(404, "없는 근무 유형입니다.");
    // 쓰는 근무 신청이 있으면 서버는 행을 남긴 채 숨긴다. mock 은 목록에서만 빼면 같은 결과다.
    db.scheduleCategories.splice(index, 1);
    return new HttpResponse(null, { status: 204 });
  }),

  /* ── 휴가 유형 — 사무소가 늘린다 ────────────────────────────────── */

  http.get(`${BASE}/vacation-kinds/`, async () => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const results = [...db.vacationKinds].sort((a, b) => a.sort_order - b.sort_order);
    return HttpResponse.json({ results });
  }),

  http.post(`${BASE}/admin/vacation-kinds/`, async ({ request }) => {
    await latency();
    const admin = requireHr();
    if (admin instanceof Response) return admin;
    const input = (await request.json()) as VacationKindInput;
    const name = input.name.trim();
    if (!name) return fail(422, "이름을 적어 주세요.");
    if (db.vacationKinds.some((row) => row.name === name))
      return fail(409, "같은 이름의 휴가 유형이 있습니다.");
    const unit = badUnit(input.unit_days);
    if (unit) return unit;
    const after = afterDays(input.attachment_after, input.attachment_after_days);
    if (after instanceof Response) return after;
    const made: VacationKind = {
      ...input,
      is_annual: false,
      attachment_after_days: after,
      id: `vk-${crypto.randomUUID().slice(0, 8)}`,
      name,
      sort_order: nextOrder(db.vacationKinds),
    };
    db.vacationKinds.push(made);
    return HttpResponse.json(made, { status: 201 });
  }),

  http.patch(`${BASE}/admin/vacation-kinds/:id/`, async ({ params, request }) => {
    await latency();
    const admin = requireHr();
    if (admin instanceof Response) return admin;
    const kind = db.vacationKinds.find((row) => row.id === params.id);
    if (!kind) return fail(404, "없는 휴가 유형입니다.");
    const patch = (await request.json()) as VacationKindPatch;
    if (patch.name && db.vacationKinds.some((row) => row.id !== kind.id && row.name === patch.name))
      return fail(409, "같은 이름의 휴가 유형이 있습니다.");
    const unit = badUnit(patch.unit_days);
    if (unit) return unit;
    if (
      kind.is_annual &&
      ((patch.unit_days !== undefined && patch.unit_days !== kind.unit_days) ||
        patch.deducts_annual === false)
    )
      return fail(422, "연차의 신청 단위와 연차 차감은 바꿀 수 없습니다.");
    const after = afterDays(
      patch.attachment_after ?? kind.attachment_after,
      "attachment_after_days" in patch ? patch.attachment_after_days : kind.attachment_after_days
    );
    if (after instanceof Response) return after;
    Object.assign(kind, patch, { attachment_after_days: after, is_annual: kind.is_annual });
    return HttpResponse.json(kind);
  }),

  http.delete(`${BASE}/admin/vacation-kinds/:id/`, async ({ params }) => {
    await latency();
    const admin = requireHr();
    if (admin instanceof Response) return admin;
    const index = db.vacationKinds.findIndex((row) => row.id === params.id);
    if (index < 0) return fail(404, "없는 휴가 유형입니다.");
    if (db.vacationKinds[index].is_annual) return fail(409, "연차는 지울 수 없습니다.");
    // 쓰는 신청이 있으면 서버는 행을 남긴 채 숨긴다. mock 은 목록에서만 빼면 같은 결과다.
    db.vacationKinds.splice(index, 1);
    return new HttpResponse(null, { status: 204 });
  }),

  /* ── 첨부파일 — 먼저 올리고 신청할 때 id 를 보낸다 ─────────────────── */

  http.post(`${BASE}/attendance-attachments/`, async ({ request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const file = (await request.formData()).get("file");
    if (!(file instanceof File) || !ATTACHMENT_TYPES.includes(file.type))
      return fail(422, "이미지나 PDF 파일만 첨부할 수 있습니다.");
    if (file.size > ATTACHMENT_BYTES) return fail(422, "첨부파일은 한 개에 10MB까지입니다.");
    const id = `att-${crypto.randomUUID().slice(0, 8)}`;
    const made: AttendanceAttachment = {
      id,
      stage: null,
      file_name: file.name,
      content_type: file.type,
      byte_size: file.size,
      url: `${BASE}/attendance-attachments/${id}/content/`,
    };
    db.attendanceAttachments.push({
      ...made,
      uploaded_by: user.id,
      owner: null,
      data: await file.arrayBuffer(),
    });
    return HttpResponse.json(made, { status: 201 });
  }),

  http.get(`${BASE}/attendance-attachments/:id/content/`, async ({ params }) => {
    const user = requireUser();
    if (user instanceof Response) return user;
    const row = db.attendanceAttachments.find((item) => item.id === params.id);
    const hr = user.role === "admin" || user.is_hr;
    if (!row || (row.uploaded_by !== user.id && !hr))
      return fail(404, "첨부파일을 찾을 수 없습니다.");
    return new HttpResponse(row.data, { headers: { "Content-Type": row.content_type } });
  }),

  /* ── 근무 신청 — 외근·출장·기타. 승인이 필요한 근무 유형만 결재를 받는다 ── */

  http.get(`${BASE}/schedules/`, async () => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const results = db.schedules
      .filter((schedule) => schedule.user_id === user.id)
      .sort((a, b) => (a.starts_at < b.starts_at ? 1 : -1));
    return HttpResponse.json({ results });
  }),

  http.post(`${BASE}/schedules/`, async ({ request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const input = (await request.json()) as ScheduleInput;
    if (!input.reason?.trim()) return fail(422, "사유를 적어 주세요.");
    const category = db.scheduleCategories.find((row) => row.id === input.category_id);
    if (!category) return fail(404, "없는 근무 유형입니다.");
    // 「종일」은 날짜만 받아 근무 시간(09:00–18:00)으로 채운다 — 서버와 같다.
    if (category.all_day && !(input.start_date && input.end_date))
      return fail(422, "종일 근무 유형은 날짜만 고릅니다.");
    if (!category.all_day && !(input.starts_at && input.ends_at))
      return fail(422, "시간 근무 유형은 시작 · 종료 시각까지 고릅니다.");
    const startsAt = category.all_day ? `${input.start_date}T09:00:00` : input.starts_at!;
    const endsAt = category.all_day ? `${input.end_date}T18:00:00` : input.ends_at!;
    if (endsAt <= startsAt) return fail(422, "종료가 시작보다 앞이에요.");
    if (!HALF_HOUR.test(startsAt.slice(11, 16)) || !HALF_HOUR.test(endsAt.slice(11, 16)))
      return fail(422, "시각은 30분 단위로 고르세요.");
    const id = `schedule-new-${++seq}`;
    const attachments = attachFiles(
      "before",
      category.attachment_before,
      input.attachment_ids,
      user.id,
      id
    );
    if (attachments instanceof Response) return attachments;
    const made: UserSchedule = {
      id,
      user_id: user.id,
      user_name: nameOf(user.id),
      category_id: category.id,
      category_name: category.name,
      category_color: category.color,
      starts_at: inOffice(startsAt),
      ends_at: inOffice(endsAt),
      all_day: category.all_day,
      project_id: input.project_id ?? null,
      reason: input.reason.trim(),
      // 승인이 필요 없는 근무 유형이면 넣는 즉시 확정이다.
      status: category.requires_approval ? "pending" : "approved",
      approver_id: null,
      decided_at: null,
      decision_note: "",
      cancelled_at: null,
      created_at: new Date().toISOString(),
      attachments,
      attachment_due: category.attachment_after,
    };
    db.schedules.push(made);
    return HttpResponse.json(made, { status: 201 });
  }),

  http.post(`${BASE}/schedules/:id/attachments/`, async ({ params, request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const found = db.schedules.find((s) => s.id === params.id && s.user_id === user.id);
    if (!found) return fail(404, "없는 근무 신청입니다.");
    const category = db.scheduleCategories.find((row) => row.id === found.category_id);
    if (!category?.attachment_after) return fail(409, "이 유형은 사후 첨부를 받지 않습니다.");
    if (found.cancelled_at || found.status === "rejected")
      return fail(409, "취소되거나 반려된 신청에는 첨부할 수 없습니다.");
    const { attachment_ids } = (await request.json()) as { attachment_ids: string[] };
    const attached = attachFiles("after", false, attachment_ids, user.id, found.id);
    if (attached instanceof Response) return attached;
    found.attachments = [...found.attachments, ...attached];
    found.attachment_due = false;
    return HttpResponse.json(found);
  }),

  http.get(`${BASE}/schedules/calendar/`, async ({ request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const query = new URL(request.url).searchParams;
    const year = Number(query.get("year"));
    const month = Number(query.get("month"));
    const results = db.schedules.filter(
      (schedule) =>
        !schedule.cancelled_at &&
        schedule.status === "approved" &&
        inMonth(schedule.starts_at.slice(0, 10), schedule.ends_at.slice(0, 10), year, month)
    );
    return HttpResponse.json({ results });
  }),

  http.get(`${BASE}/schedules/:id/`, async ({ params }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const found = db.schedules.find((s) => s.id === params.id && s.user_id === user.id);
    return found ? HttpResponse.json(found) : fail(404, "없는 근무 신청입니다.");
  }),

  http.post(`${BASE}/schedules/:id/cancel/`, async ({ params }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const found = db.schedules.find((s) => s.id === params.id && s.user_id === user.id);
    if (!found) return fail(404, "없는 근무 신청입니다.");
    // 근무 신청은 결재 없이 바로 취소된다. 행은 남기고 취소 시각만 채운다.
    if (found.cancelled_at) return fail(409, "이미 취소한 근무 신청이에요.");
    found.cancelled_at = new Date().toISOString();
    return HttpResponse.json(found);
  }),

  http.get(`${BASE}/admin/schedules/`, async ({ request }) => {
    await latency();
    const admin = requireHr();
    if (admin instanceof Response) return admin;
    const status = new URL(request.url).searchParams.get("status");
    const results = db.schedules
      .filter((schedule) => !schedule.cancelled_at && (!status || schedule.status === status))
      .sort((a, b) => (a.starts_at < b.starts_at ? 1 : -1));
    return HttpResponse.json({ results });
  }),

  http.post(`${BASE}/admin/schedules/:id/decision/`, async ({ params, request }) => {
    await latency();
    const admin = requireHr();
    if (admin instanceof Response) return admin;
    const found = db.schedules.find((s) => s.id === params.id);
    if (!found) return fail(404, "없는 근무 신청입니다.");
    if (found.status !== "pending" || found.cancelled_at)
      return fail(409, "대기 중인 근무 신청만 승인하거나 반려할 수 있습니다.");
    const decision = (await request.json()) as AttendanceDecision;
    if (!decision.approve && !decision.note?.trim())
      return fail(422, "반려하려면 사유가 필요합니다.");
    found.status = decision.approve ? "approved" : "rejected";
    found.approver_id = admin.id;
    found.decided_at = new Date().toISOString();
    found.decision_note = decision.note?.trim() ?? "";
    return HttpResponse.json(found);
  }),

  /* ── 휴일 ────────────────────────────────────────────────────────── */

  http.get(`${BASE}/holidays/`, async ({ request }) => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const year = new URL(request.url).searchParams.get("year");
    const results = db.holidays
      .filter((holiday) => holiday.holiday_date.startsWith(String(year)))
      .sort((a, b) => a.holiday_date.localeCompare(b.holiday_date));
    return HttpResponse.json({ results });
  }),

  http.post(`${BASE}/admin/holidays/`, async ({ request }) => {
    await latency();
    const admin = requireHr();
    if (admin instanceof Response) return admin;
    const input = (await request.json()) as HolidayInput;
    if (!input.holiday_date || !input.name?.trim()) return fail(422, "날짜와 이름을 적어 주세요.");
    if (db.holidays.some((holiday) => holiday.holiday_date === input.holiday_date))
      return fail(409, "그날은 이미 휴일이에요.");
    const made = {
      holiday_date: input.holiday_date,
      name: input.name.trim(),
      kind: input.kind,
    };
    db.holidays.push(made);
    db.holidays.sort((a, b) => a.holiday_date.localeCompare(b.holiday_date));
    return HttpResponse.json(made, { status: 201 });
  }),

  http.delete(`${BASE}/admin/holidays/:date/`, async ({ params }) => {
    await latency();
    const admin = requireHr();
    if (admin instanceof Response) return admin;
    const index = db.holidays.findIndex((holiday) => holiday.holiday_date === params.date);
    if (index < 0) return fail(404, "없는 휴일입니다.");
    db.holidays.splice(index, 1);
    return new HttpResponse(null, { status: 204 });
  }),

  /* ── 연차 부여 ───────────────────────────────────────────────────── */

  http.get(`${BASE}/vacation-grants/me/`, async () => {
    await latency();
    const user = requireUser();
    if (user instanceof Response) return user;
    const results = db.vacationGrants.filter((grant) => grant.user_id === user.id).map(withUsage);
    return HttpResponse.json({ results });
  }),

  http.get(`${BASE}/admin/vacation-grants/`, async ({ request }) => {
    await latency();
    const admin = requireHr();
    if (admin instanceof Response) return admin;
    const userId = new URL(request.url).searchParams.get("user_id");
    const results = db.vacationGrants
      .filter((grant) => !userId || grant.user_id === userId)
      .map(withUsage)
      .sort((a, b) => a.user_name.localeCompare(b.user_name, "ko"));
    return HttpResponse.json({ results });
  }),

  http.post(`${BASE}/admin/vacation-grants/`, async ({ request }) => {
    await latency();
    const admin = requireHr();
    if (admin instanceof Response) return admin;
    const input = (await request.json()) as GrantInput;
    if (!input.user_id) return fail(422, "직원을 고르세요.");
    if (input.period_to <= input.period_from) return fail(422, "기간을 확인해 주세요.");
    if ((input.adjusted ?? 0) !== 0 && !input.note?.trim())
      return fail(422, "조정에는 사유가 필요해요.");
    // 같은 직원의 부여 기간은 겹칠 수 없다.
    if (
      db.vacationGrants.some(
        (grant) =>
          grant.user_id === input.user_id &&
          grant.period_from < input.period_to &&
          input.period_from < grant.period_to
      )
    )
      return fail(409, "그 기간에 겹치는 부여가 있어요.");
    const made = {
      id: `grant-new-${++seq}`,
      user_id: input.user_id,
      user_name: nameOf(input.user_id),
      period_from: input.period_from,
      period_to: input.period_to,
      granted: input.granted,
      adjusted: input.adjusted ?? 0,
      prior_used: input.prior_used ?? 0,
      note: input.note?.trim() ?? "",
    };
    db.vacationGrants.push(made);
    return HttpResponse.json(withUsage(made), { status: 201 });
  }),

  http.patch(`${BASE}/admin/vacation-grants/:id/`, async ({ params, request }) => {
    await latency();
    const admin = requireHr();
    if (admin instanceof Response) return admin;
    const found = db.vacationGrants.find((grant) => grant.id === params.id);
    if (!found) return fail(404, "없는 부여입니다.");
    const patch = (await request.json()) as GrantPatch;
    if (patch.granted !== undefined) found.granted = patch.granted;
    if (patch.adjusted !== undefined) found.adjusted = patch.adjusted;
    if (patch.prior_used !== undefined) found.prior_used = patch.prior_used;
    if (patch.note !== undefined) found.note = patch.note.trim();
    if (found.adjusted !== 0 && !found.note) return fail(422, "조정에는 사유가 필요해요.");
    return HttpResponse.json(withUsage(found));
  }),

  /* ── 관리자 휴가 결정 ────────────────────────────────────────────── */

  http.get(`${BASE}/admin/vacations/`, async ({ request }) => {
    await latency();
    const admin = requireHr();
    if (admin instanceof Response) return admin;
    const query = new URL(request.url).searchParams;
    const status = query.get("status");
    const userId = query.get("user_id");
    const cancelRequested = query.get("cancel_requested");
    const results = db.vacations
      .filter((vacation) => !status || vacation.status === status)
      .filter((vacation) => !userId || vacation.user_id === userId)
      .filter(
        (vacation) =>
          cancelRequested === null ||
          (cancelRequested === "true") === (vacation.cancel_requested_at !== null)
      )
      .sort((a, b) => (a.start_date < b.start_date ? 1 : -1));
    return HttpResponse.json({ results });
  }),

  http.post(`${BASE}/admin/vacations/:id/decision/`, async ({ params, request }) => {
    await latency();
    const admin = requireHr();
    if (admin instanceof Response) return admin;
    const found = db.vacations.find((vacation) => vacation.id === params.id);
    if (!found) return fail(404, "없는 휴가입니다.");
    if (found.status !== "pending") return fail(409, "이미 처리된 신청이에요.");
    const decision = (await request.json()) as AttendanceDecision;
    if (!decision.approve && !decision.note?.trim()) return fail(422, "반려 사유를 적어 주세요.");
    found.status = decision.approve ? "approved" : "rejected";
    found.approver_id = admin.id;
    found.decided_at = new Date().toISOString();
    found.decision_note = decision.note?.trim() ?? "";
    return HttpResponse.json(found);
  }),

  http.post(`${BASE}/admin/vacations/:id/cancel-decision/`, async ({ params, request }) => {
    await latency();
    const admin = requireHr();
    if (admin instanceof Response) return admin;
    const found = db.vacations.find((vacation) => vacation.id === params.id);
    if (!found) return fail(404, "없는 휴가입니다.");
    if (!found.cancel_requested_at) return fail(409, "취소 신청이 없어요.");
    const decision = (await request.json()) as AttendanceDecision;
    if (!decision.approve && !decision.note?.trim()) return fail(422, "반려 사유를 적어 주세요.");
    if (decision.approve) found.status = "cancelled";
    found.cancel_requested_at = null;
    found.decision_note = decision.note?.trim() ?? "";
    return HttpResponse.json(found);
  }),
];
